/**
 * @file Xoe actions API — confirm, dismiss, or record execution of a proposed assistant action.
 * @module api/xoe/XoeActionsApi
 */

import { ENV } from '../../config/runtimeEnv';
import { http } from '../../lib/http/client';

const HEALTHCARE_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

export interface ConfirmXoeActionPayload {
	/** ID of the assistant message that carries the tool_calls. */
	messageId: number;
	actionId: string;
	draft: Record<string, unknown>;
	conversationUuid: string;
	safetyOverride?: boolean;
}

export interface ConfirmXoeActionResponse {
	linked_item_type: string | null;
	linked_item_id: number | null;
	confirmed_at: string;
	already_confirmed?: boolean;
}

/** Confirm a proposed action from an assistant message. */
export const confirmXoeAction = async (
	payload: ConfirmXoeActionPayload,
): Promise<ConfirmXoeActionResponse> => {
	const { messageId, conversationUuid: _conversationUuid, actionId, safetyOverride, draft, ...rest } = payload;
	const finalDraft = safetyOverride ? { ...draft, safety_override: true } : draft;
	const body = { ...rest, draft: finalDraft, action_id: actionId };
	const response = await http.post(
		`${HEALTHCARE_BASE_URL}/practitioner/xoe/actions/${messageId}/confirm`,
		body,
	);
	// Backend returns the object BARE (no `data` wrapper).
	return response.data;
};

export interface DismissXoeActionPayload {
	/** ID of the assistant message that carries the tool_calls. */
	messageId: number;
	actionId: string;
	/** UUID of the parent conversation — used by the hook to patch the cached thread; never sent. */
	conversationUuid?: string;
}

export interface DismissXoeActionResponse {
	status: 'dismissed';
	dismissed_at: string;
	/** Present when the proposal was already dismissed — the endpoint is idempotent. */
	already_dismissed?: boolean;
}

/**
 * Dismiss (reject) a proposed action.
 */
export const dismissXoeAction = async (
	payload: DismissXoeActionPayload,
): Promise<DismissXoeActionResponse> => {
	const { messageId, actionId } = payload;
	const response = await http.post(
		`${HEALTHCARE_BASE_URL}/practitioner/xoe/actions/${messageId}/dismiss`,
		{ action_id: actionId },
	);
	// Backend returns the object BARE (no `data` wrapper).
	return response.data;
};

export interface RecordOpenActionPayload {
	/** ID of the assistant message that carries the tool_calls. */
	messageId: number;
	action_id: string;
}

export interface RecordOpenActionResponse {
	status: 'executed';
	executed_at: string | null;
	already_recorded?: boolean;
}

/**
 * Record that an open/navigation action was executed (audit only — no clinical record).
 * Called best-effort when the practitioner clicks "Open" on an open action card so
 * executed open actions leave a server-side audit trail (defect 1C).
 */
export const recordXoeOpenAction = async (
	payload: RecordOpenActionPayload,
): Promise<RecordOpenActionResponse> => {
	const { messageId, ...body } = payload;
	const response = await http.post(
		`${HEALTHCARE_BASE_URL}/practitioner/xoe/actions/${messageId}/open`,
		body,
	);
	// Backend returns the object BARE (no `data` wrapper).
	return response.data;
};
